/**
 * RWBR — core domain types.
 *
 * Field geometry, confirmed from Boards A and B:
 *   LEADER  = 1st
 *   TOP     = 2nd - 7th    (6 cars, Board A, selected by the white die 1-6)
 *   MIDDLE  = 8th - 19th   (12 cars in 6 boxes of 2, odd/even halves)
 *   BOTTOM  = 20th back    (a face-up stack, last place on top)
 *
 * One turn = 10 MILES, not laps (rulebook p.ii).
 */

import type { AlternateCard } from './altCards.ts';
import type { Manufacturer } from './livery.ts';

export const QUALITIES = ['REFLEX', 'CONTROL', 'SKILL', 'STAR', 'SMART', 'HEAT'] as const;
export type Quality = (typeof QUALITIES)[number];

/** A RACE CAM cell resolves to one of these. TV is granted temporarily by Race Week. */
export type RaceCamResult = Quality | 'TV' | 'RARE' | 'PROBLEM' | 'TROUBLE';

export type Group = 'leader' | 'top' | 'middle' | 'bottom';
export type PitCrewGrade = 'A' | 'B' | 'C' | 'D' | 'E';
export type Trophy = 'gold' | 'silver' | 'bronze' | null;

/**
 * EZRA's eight driver traits (EZRA-S2 §4.0). Declared here rather than in
 * `traits.ts` only to keep the card free of an import cycle; `traits.ts` owns
 * every rule about them and re-exports this type.
 */
export type TraitCode = 'AW' | 'DS' | 'EN' | 'FN' | 'RC' | 'RS' | 'SS' | 'ST';

export interface DriverCard {
  driverId: string;
  name: string;
  carNumber: string;
  /** Red die must be <= this to have the speed for a challenge. */
  gasPedal: number;
  /** Blue die must be > this for a challenge to succeed against this driver. */
  brakePedal: number;
  qualities: Quality[];
  /** A starred quality grants a second challenge (rulebook p.vii). */
  starred: Quality[];
  /** 2d6 must be <= this to escape TROUBLE. */
  escape: number;
  /** 2d6 must be <= this on a pit turn to earn a performance chip. */
  fastPit: number;
  /** EZRA split grade. null = a double fast pit is not possible for this crew. */
  fastPitDouble: number | null;
  trophy: Trophy;
  /** Belongs to the team (D-007); carried here for convenience during a race. */
  pitCrewGrade: PitCrewGrade;
  /** Red '6' in any challenge or duel triggers an immediate PROBLEM (p.xvi). */
  triangle: boolean;
  /**
   * THE GREEN THUMBS-UP TRIANGLE — new to the 2025 Pro Season set (D-EZ-76).
   *
   * ⚠ A DIFFERENT SYMBOL AND THE OPPOSITE RULE FROM `triangle`. The old one is
   * a warning: a red 6 is an immediate PROBLEM. This one is an EXEMPTION, and
   * the set's notes give it in full:
   *
   *   *"any driver who, in real-life, did not have a race in which they failed
   *   to finish because of mechanical issues, has been given a green 'thumbs
   *   up' triangle. For these drivers, if the pit crew fails the REPAIR roll for
   *   a PROBLEM result, rather than being knocked out of the race, they instead
   *   go to the top (last place) of the BOTTOM group stack, but lose their
   *   ability to challenge or defend for the rest of the race."*
   *
   * It replaces the old "DNF Exemption List" the sets used to print — the notes
   * say keeping that list *"become more of a hassle to keep track"*. And it is
   * NOT a free pass: the same paragraph ends *"NOTE that it is still possible
   * for these drivers to be knocked out of a race on PROBLEM rolls of 13, 21 or
   * 66, which are 'out of race' results."* Only a FAILED REPAIR is exempted.
   */
  dnfExempt?: boolean;
  /**
   * EZRA's driver trait, printed in the top-right corner of an EZRA card. NOT
   * one of the six base qualities — EZRA's own addition, absent from every
   * published PLAAY card.
   *
   * ONE FIELD, NOT EIGHT FLAGS, because EZRA-S2 §4.0 says "No driver can have
   * more than one (1) trait" — a set of booleans would make the forbidden state
   * representable. `traits.ts` holds the definitions and the assignment rules.
   */
  trait?: TraitCode;
  /**
   * A SECOND CARD, AND THE RACES IT APPLIES TO — D-EZ-76.
   *
   * The 2025 set prints one for five drivers: Larson and Byron in the playoffs,
   * Wallace on superspeedways, Austin Dillon on short tracks, and Shane van
   * Gisbergen on road courses, where the set gives him a whole different card.
   *
   * ⚠ IT IS STORED AS A DIFFERENCE FROM THIS CARD, NOT AS A SECOND SET OF
   * NUMBERS, and it lives HERE — on the card — rather than in the season's
   * options. Both of those are load-bearing; `altCards.ts` explains why at
   * length. The short version: a difference still means something after the
   * winter has re-rated him, and a card travels every path a driver takes
   * without anybody having to plumb it.
   */
  altCard?: AlternateCard;
  /**
   * The marque on the nose tab, when the card prints one.
   *
   * A generated driver has none and the livery hashes him one, which is the
   * right answer for an invented field. A published card is not invented, so
   * where the set says Ford this says Ford — see `liveryForDriver`.
   */
  make?: Manufacturer;
  teamId?: string;
  /**
   * D-EZ-93 — the driver's age this season. Not a printed card field and read
   * by no race rule: it drives the winter's performance curve and retirement.
   * Absent on a founding card; `aging.ts`'s `ageOf` supplies a stable one.
   */
  age?: number;
}

/**
 * Effects a POST RACE INTERVIEW result grants for ONE race that a card cannot
 * express, because every one of them is about *when* — "For the first third of
 * the race", "(one time)", "on first ELIGIBLE pit roll", "first escape roll".
 *
 * Declared here rather than in `interviewEffects.ts` for exactly the reason
 * `TraitCode` is: `Car` has to carry it, and `types.ts` must not import the
 * module that interprets it. `interviewEffects.ts` owns every rule about these
 * and re-exports the type.
 *
 * D-EZ-15: single-race, always. Nothing here survives a checkered flag.
 */
export interface RaceGrants {
  /** "Ignore any OUT result (one time) from the Problem or Feud/Fume chart." */
  ignoreOutOnce?: boolean;
  /** "Racer automatically succeeds first escape roll." */
  autoEscapeOnce?: boolean;
  /** "Racer automatically wins Quality Challenge. Ignore performance chips." */
  autoWinChallengeOnce?: boolean;
  /** "On first ELIGIBLE pit roll for Fast Pit, Racer automatically succeeds." */
  fastPitAuto?: 'single' | 'double';
  /** "Racer's ... repair ... # is increased by 1 or 2." Whole race. */
  repairBonus?: number;
  /** "For the first third of the race, Racer is given one extra Gas Pedal." */
  firstThirdGas?: number;
  /** "For the first third of the race, Racer is given one extra Brake." */
  firstThirdBrake?: number;
  /**
   * "FIRST DUEL- ROLL ON FEUD/FUME CHART- One first Duel roll, immediately roll
   * on Feud/Fume Chart for Racers involved."
   *
   * Lives here for exactly the reason the rest of this interface exists: it is
   * about WHEN. It is his FIRST duel of the race and it is ONE of them, which no
   * card can express and no season-level list can enforce — the season knows he
   * has the effect, only the turn loop knows which duel is his first.
   */
  firstDuelFeudFume?: boolean;
}

/** A car's live state during a race. */
export interface Car {
  card: DriverCard;
  /**
   * One-race POST RACE INTERVIEW grants still unspent. Consumed where they are
   * used and struck off as they go, so "(one time)" really is one time.
   */
  grants?: RaceGrants;
  /** 1-based running order. 1 = leader. */
  position: number;
  /** Performance chips in hand and spendable RIGHT NOW. Not carried in the BOTTOM group (p.xv). */
  chips: number;
  /**
   * How many of `chips` were awarded by a driver TRAIT (FN, ST). EZRA-S2 §4.0
   * suspends those rather than forfeiting them on a trip to the BOTTOM group,
   * so a demotion has to know how many to protect. See `chips.ts`.
   */
  traitChips?: number;
  /**
   * Trait chips parked because their owner is in the BOTTOM or LAP DOWN group.
   * "The Performance Chip is not lost" (EZRA-S2 §4.0) — held here, unspendable
   * by construction, returned to `chips` when he climbs back out.
   */
  heldChips?: number;
  /**
   * Chips in excess of two, held in reserve (EZRA-S2, D-EZ-28).
   *
   * "Reserve pc's have no value until activated" — so they are deliberately NOT
   * in `chips`, and nothing that spends a chip can see them. One is activated at
   * the start of a turn once the active pile is empty; all of them are lost the
   * moment their owner loses track position; none survives to the next race.
   */
  reserveChips?: number;
  /**
   * BGC performance chips — EZRA-S2 §3.12's *"token of a different color"*
   * (D-EZ-39).
   *
   * A THIRD kind of chip, and deliberately NOT in `chips`: it is spendable only
   * in the BOTTOM group and only during a Bottom Group Churn, so a count that
   * every chip contest in the engine could see would be wrong nearly all of the
   * time. `chips.ts` owns the whole rule — `bgChipsSpendable` is the only read
   * that yields a spendable number, `awardBgChip` is the only way one comes into
   * existence, and `restoreChips` destroys it the moment its owner leaves the
   * BOTTOM group.
   */
  bgChips?: number;
  /** Temporary quality granted by the Race Week TV list. */
  tvQuality: Quality | null;
  status: 'running' | 'out';
  outReason?: 'trouble' | 'problem' | 'feud' | 'fuel' | 'black-flag' | 'repair-failed' | 'injury';
  outTurn?: number;
  /**
   * HE IS STILL OUT THERE AND HE IS NO LONGER RACING — D-EZ-76.
   *
   * Set when a green-triangle driver survives a failed REPAIR. He keeps his
   * place in the field and finishes the race, and he *"lose[s] their ability to
   * challenge or defend for the rest of the race"* — so he neither starts a
   * quality challenge nor is picked as the man to beat.
   *
   * ⚠ NOT THE SAME AS BEING OUT. He still scores, still counts as running, and
   * still shows on the boards. The whole point of the exemption is that his day
   * is ruined rather than ended.
   */
  cannotRace?: boolean;
  /** Set when the laps-down optional rule puts a car off the lead lap. */
  lapsDown: boolean;
  /** Consecutive turns this car has led — 10 earns a bonus chip (p.xv). */
  consecutiveTurnsLed: number;
  turnsLed: number;
}

export interface FeudEntry {
  /** Two drivers who are feuding with each other. */
  a: string;
  b: string;
}

export interface RaceState {
  raceId: string;
  trackId: string;
  raceName: string;
  totalTurns: number;
  turn: number;
  /** Running order, index 0 = leader. Only cars still running appear. */
  grid: Car[];
  /** Cars that are out, kept for the finishing order. */
  retired: Car[];
  /** Turns on which a pit stop is scheduled (p.xi). */
  pitTurns: number[];
  /** Remaining yellow-flag turns to burn before green resumes. */
  yellowTurnsRemaining: number;
  feuds: FeudEntry[];
  /** Drivers fuming — they may vent on anyone in their own group (p.x). */
  fuming: string[];
  finished: boolean;
  /** Drivers who skipped the final pit and are running on fumes (p.xv). */
  lowFuel: string[];
  /**
   * D-EZ-88 — a RARE RESULTS "Rain threatens event": the turn at whose END the
   * per-turn rain die starts being rolled. Absent = no rain threat this race.
   */
  rainCheckFrom?: number;
}

/** Which group a 1-based position falls in. */
export function groupOf(position: number): Group {
  if (position === 1) return 'leader';
  if (position <= 7) return 'top';
  if (position <= 19) return 'middle';
  return 'bottom';
}

/** Inclusive 1-based position bounds for a group. */
export function groupRange(group: Exclude<Group, 'leader'>): [number, number] {
  if (group === 'top') return [2, 7];
  if (group === 'middle') return [8, 19];
  return [20, Number.MAX_SAFE_INTEGER];
}

/** Every quality this car counts as holding, including a Race Week TV grant. */
export function carQualities(car: Car): Quality[] {
  return car.tvQuality && !car.card.qualities.includes(car.tvQuality)
    ? [...car.card.qualities, car.tvQuality]
    : car.card.qualities;
}

/** Does this car hold the called quality this turn? */
export function hasQuality(car: Car, q: Quality | 'TV'): boolean {
  if (q === 'TV') return car.tvQuality !== null;
  return carQualities(car).includes(q);
}

/** Is the called quality starred for this car? A star grants a second challenge. */
export function isStarred(car: Car, q: Quality | 'TV'): boolean {
  return q !== 'TV' && car.card.starred.includes(q);
}
